/**
 * Content Statistics Utilities
 *
 * Helper functions for calculating word count, reading time, and other
 * content-related statistics for blog posts.
 */

/**
 * Strip HTML tags from content string
 * @param html - HTML content string
 * @returns Plain text without HTML tags
 */
export const stripHtml = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

/**
 * Calculate word count from plain text
 * Filters out empty strings from split results
 * @param text - Plain text content
 * @returns Number of words
 */
export const calculateWordCount = (text: string): number => {
  return text.split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Calculate estimated reading time in minutes
 * Based on average reading speed of 200 words per minute
 * @param wordCount - Number of words
 * @returns Reading time in minutes (minimum 1)
 */
export const calculateReadingTime = (wordCount: number): number => {
  return Math.max(1, Math.ceil(wordCount / 200));
};

/**
 * Calculate character count from plain text
 * @param text - Plain text content
 * @returns Number of characters
 */
export const calculateCharacterCount = (text: string): number => {
  return text.length;
};

/**
 * Get all content statistics at once
 * @param htmlContent - HTML content string
 * @returns Object with wordCount, characterCount, and readingTime
 */
export const getContentStats = (htmlContent: string) => {
  const plainText = stripHtml(htmlContent);
  const wordCount = calculateWordCount(plainText);

  return {
    wordCount,
    characterCount: calculateCharacterCount(plainText),
    readingTime: calculateReadingTime(wordCount),
  };
};
